import { Icon, SocialMediaTabsCustom, Typography } from '@/components/common';
import Icons from '@/const/icons';
import { useHandleError } from '@/hooks/useHandleError';
import {
	AdCampaign,
	Campaign,
	CampaignChannels,
	CampaignTabsDetails,
} from '@/interfaces';
import { Tab } from '@headlessui/react';
import clsx from 'clsx';
import * as React from 'react';

export interface ChannelTabList {
	name: string;
	label: string;
	approved: boolean;
	hasError: boolean;
	channels: {
		name: string;
		label: string;
		icon: React.ReactElement;
		approved: boolean;
		ads: AdCampaign[];
		hasError: boolean;
	}[];
}

interface ChannelTabsProps {
	campaignDetails?: Campaign;
	selectedIndex: number;
	clickBack?: () => void;
	tabsList: ChannelTabList[];
	setTabsList: (value: ChannelTabList[]) => void;
	itemSelected: string;
	setItemSelected: (value: string) => void;
}

export const ChannelTabsCustom: React.FC<ChannelTabsProps> = ({
	campaignDetails,
	selectedIndex,
	clickBack,
	tabsList,
	setTabsList,
	itemSelected,
	setItemSelected,
}) => {
	const { marketingErrors } = useHandleError();

	const derIcons: Record<CampaignChannels, React.ReactNode> = {
		FACEBOOK: <Icon src={Icons.facebook2} className="!w-5 !h-5" />,
		FACEBOOK_ADS: <Icon src={Icons.facebook2} className="!w-5 !h-5" />,
		INSTAGRAM: <Icon src={Icons.instagram} className="!size-6" />,
		INSTAGRAM_ADS: <Icon src={Icons.instagram} className="!size-6" />,
		MARKETING_EMAIL: (
			<Icon src={Icons.sms} className="!size-6 text-purple-150" />
		),
		GOOGLE_ADS: <Icon src={Icons.googleAds} className="!size-5" />,
		GOOGLE_SEARCH: <Icon src={Icons.googleAds} className="!size-5" />,
	};

	const getIsTabApproved = (tabs: CampaignTabsDetails[]): boolean => {
		return tabs.every((item) => item.approved);
	};

	React.useEffect(() => {
		if (campaignDetails?.tabs) {
			const tabOrder = ['ads', 'posts', 'email']; // Define the desired order

			const groupedTabs = Object.entries(campaignDetails.tabs)
				.map(([tabName, items]) => {
					if (!items || !items.length) return null;

					// get all types by tab
					const typesArray: CampaignChannels[] = items.map(
						(item: CampaignTabsDetails) => item.type
					);

					// check if there is any channel type with error
					const hasError = marketingErrors.some(
						(item) =>
							item.input_data?.type &&
							item.entity_id === campaignDetails.id &&
							typesArray.includes(item.input_data?.type)
					);

					return {
						name: tabName.toLowerCase(),
						label: tabName.toLowerCase(),
						approved: getIsTabApproved(items),
						hasError: hasError,
						channels: items.map((item: CampaignTabsDetails) => ({
							name: item.type?.toLowerCase() || tabName,
							label: item.type?.toLowerCase() || tabName,
							icon: derIcons[item.type as keyof typeof derIcons],
							approved: item.approved,
							ads: item.ads || [],
						})),
					};
				})
				.filter(Boolean) as ChannelTabList[];

			// Sort tabs based on the predefined order
			const sortedTabs = groupedTabs.sort(
				(a, b) => tabOrder.indexOf(a.name) - tabOrder.indexOf(b.name)
			);

			setTabsList(sortedTabs);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campaignDetails]);

	React.useEffect(() => {
		if (tabsList.length > 0) {
			setItemSelected(tabsList[0]?.channels[0]?.name);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tabsList]);

	return (
		<Tab.List className={' flex mx-7 relative gap-6'}>
			{tabsList.map((item, index) => {
				return (
					<SocialMediaTabsCustom
						key={'custom-tab-' + index}
						channelItem={item}
						className={clsx('', {
							'border-b-2 border-purple-150 bg-gray-15 ':
								selectedIndex === index,
							border: selectedIndex !== index,
						})}
						itemSelected={itemSelected}
						setItemSelected={setItemSelected}
					/>
				);
			})}

			{clickBack && (
				<button
					className="absolute right-5 flex items-center gap-2"
					onClick={clickBack}
				>
					<Icon src={Icons.arrowLeft} className={'!size-4'} />
					<Typography type="semibold-16">Back</Typography>
				</button>
			)}
		</Tab.List>
	);
};
